// src/pages/Listing/SEOTags.tsx
import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getRouteMetadata, interpolateTemplate } from '../../Routes/allRoutes';

interface Breadcrumb {
  title: string;
  link: string;
}

interface SEOTagsProps {
  title?: string; // Make optional - will auto-generate if not provided
  description?: string; // Make optional - will auto-generate if not provided
  breadcrumbs?: Breadcrumb[];
  canonicalUrl?: string;
  schemaType?: 'Organization' | 'LocalBusiness' | 'BreadcrumbList' | 'ItemList' | 'Person' | 'WebPage';
  schemaData?: any;
  keywords?: string[];
  ogImage?: string;
  noIndex?: boolean;
  alternateUrls?: { hreflang: string; href: string }[];
  // Add these new props for auto-generation
  contractorData?: any;
  customPageData?: {
    contractorCount?: number;
    category?: string;
    city?: string;
    state?: string;
  };
}

const SEOTags: React.FC<SEOTagsProps> = ({
  title: customTitle,
  description: customDescription,
  breadcrumbs,
  canonicalUrl,
  schemaType: customSchemaType,
  schemaData,
  keywords: customKeywords = [],
  ogImage,
  noIndex = false,
  alternateUrls = [],
  contractorData,
  customPageData
}) => {
  const location = useLocation();
  const params = useParams();

  // Auto-generate SEO data from route if not provided
  const routeMetadata = getRouteMetadata(location.pathname);
  
  let effectiveTitle = customTitle;
  let effectiveDescription = customDescription;
  let effectiveKeywords = customKeywords;
  let effectiveSchemaType = customSchemaType;

  if (routeMetadata && !customTitle) {
    // Prepare interpolation values - ensure all values are strings
    const values: { [key: string]: string } = {
      ...params,
      businessName: contractorData?.businessName || params.businessNameSlug || '',
      category: params.categorySlug || '',
      city: params.city || '',
      state: params.state?.toUpperCase() || ''
    };
    
    effectiveTitle = interpolateTemplate(routeMetadata.titleTemplate || '', values);
    effectiveDescription = customDescription || generateSmartDescription(params, customPageData);
    effectiveKeywords = customKeywords.length > 0 ? customKeywords : (routeMetadata.keywords?.map(keyword => 
      interpolateTemplate(keyword, values)
    ) || []);
    effectiveSchemaType = customSchemaType || routeMetadata.schemaType as any;
  }

  // Auto-generate breadcrumbs based on URL if not provided
  const generateBreadcrumbsFromUrl = (): Breadcrumb[] => {
    const path = window.location.pathname;
    const segments = path.split('/').filter(segment => segment !== '');
    const breadcrumbs: Breadcrumb[] = [];
    
    // Always start with Home
    breadcrumbs.push({ title: 'Home', link: '/' });
    
    // Handle contractor routes specifically
    if (segments[0] === 'contractors') {
      // Add "All Contractors" as the base
      breadcrumbs.push({ title: 'All Contractors', link: '/contractors' });
      
      // Process remaining segments
      let currentPath = '/contractors';
      
      for (let i = 1; i < segments.length; i++) {
        const segment = segments[i];
        currentPath += `/${segment}`;
        
        // Determine the display name based on position and content
        let displayName = '';
        
        if (i === 1) {
          // Second segment after contractors
          if (segment === 'categories') {
            displayName = 'Categories';
          } else if (segment === 'locations') {
            displayName = 'Locations';
          } else if (segment === 'category') {
            displayName = 'Category';
          } else if (segment.length === 2) {
            // State code (e.g., 'va')
            displayName = segment.toUpperCase();
          } else {
            // Fallback: capitalize and replace hyphens
            displayName = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          }
        } else if (i === 2) {
          // Third segment
          if (segments[1] === 'category') {
            // This is a category slug after /contractors/
            displayName = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          } else if (segments[1].length === 2) {
            // This is a city after a state code
            displayName = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          } else {
            displayName = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          }
        } else if (i === 3) {
          // Fourth segment - usually a category after state/city
          displayName = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        } else {
          // For any other segments, just format them nicely
          displayName = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
        
        // Don't add link for the last segment (current page)
        if (i === segments.length - 1) {
          // Last segment is just text, no link
          break;
        } else {
          breadcrumbs.push({ title: displayName, link: currentPath });
        }
      }
    } else {
      // Handle non-contractor routes
      let currentPath = '';
      
      segments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const isLast = index === segments.length - 1;
        const displayName = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        
        if (!isLast) {
          breadcrumbs.push({ title: displayName, link: currentPath });
        }
      });
    }
    
    return breadcrumbs;
  };

  // Use provided breadcrumbs or auto-generated ones
  const effectiveBreadcrumbs = breadcrumbs || generateBreadcrumbsFromUrl();

  // Generate organization schema
  const generateOrganizationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'BidBro',
    'url': 'https://www.bidbro.com',
    'logo': 'https://www.bidbro.com/assets/images/logo/bidbro-logo.png',
    'description': 'Professional contractor directory and bidding platform',
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'customer service',
      'email': 'support@bidbro.com'
    }
  });

  // Generate website schema
  const generateWebsiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'BidBro',
    'url': 'https://www.bidbro.com',
    'description': 'Find trusted local contractors on BidBro',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': 'https://www.bidbro.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  });

  // Generate breadcrumbs schema
  const generateBreadcrumbsSchema = () => {
    if (effectiveBreadcrumbs.length === 0) return null;
    
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': effectiveBreadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': crumb.title,
        'item': `${window.location.origin}${crumb.link}`
      }))
    };
  };

  // Generate schema based on type
  const generateSchema = (title: string, description: string) => {
    const schemas = [];

    // Always include organization schema
    schemas.push(generateOrganizationSchema());

    // Add website schema on homepage
    if (window.location.pathname === '/') {
      schemas.push(generateWebsiteSchema());
    }

    // Add breadcrumbs if available
    const breadcrumbSchema = generateBreadcrumbsSchema();
    if (breadcrumbSchema) {
      schemas.push(breadcrumbSchema);
    }

    // Add specific schema based on type
    if (effectiveSchemaType && schemaData) {
      switch (effectiveSchemaType) {
        case 'ItemList':
          schemas.push({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            'name': title,
            'description': description,
            'itemListElement': schemaData || []
          });
          break;
        case 'LocalBusiness':
          schemas.push({
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            'name': title,
            'description': description,
            ...schemaData
          });
          break;
        case 'WebPage':
          schemas.push({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            'name': title,
            'description': description,
            'url': canonicalUrl || window.location.href,
            ...schemaData
          });
          break;
        default:
          if (schemaData) {
            schemas.push({
              '@context': 'https://schema.org',
              '@type': effectiveSchemaType,
              ...schemaData
            });
          }
      }
    }

    return schemas.length === 1 ? schemas[0] : schemas;
  };

  useEffect(() => {
    // Ensure we have valid title and description
    const title = effectiveTitle || 'BidBro - Professional Contractor Directory';
    const description = effectiveDescription || 'Find trusted local contractors on BidBro';
    
    // Update document title with brand
    const fullTitle = title.includes('BidBro') ? title : `${title} | BidBro`;
    document.title = fullTitle;
    
    // Helper function to update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let metaTag = document.querySelector(selector);
      
      if (metaTag) {
        metaTag.setAttribute('content', content);
      } else {
        metaTag = document.createElement('meta');
        if (property) {
          metaTag.setAttribute('property', name);
        } else {
          metaTag.setAttribute('name', name);
        }
        metaTag.setAttribute('content', content);
        document.head.appendChild(metaTag);
      }
    };

    // Update meta description
    if (description) {
      updateMetaTag('description', description);
    }
    
    // Update keywords if provided
    if (effectiveKeywords.length > 0) {
      updateMetaTag('keywords', effectiveKeywords.join(', '));
    }

    // Robots meta tag
    updateMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:url', canonicalUrl || window.location.href, true);
    
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalLink) {
      canonicalLink.href = canonicalUrl || window.location.href;
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = canonicalUrl || window.location.href;
      document.head.appendChild(canonicalLink);
    }

    // Add alternate URLs for multilingual support
    alternateUrls.forEach(alt => {
      const existingLink = document.querySelector(`link[hreflang="${alt.hreflang}"]`);
      if (!existingLink) {
        const linkAlternate = document.createElement('link');
        linkAlternate.rel = 'alternate';
        linkAlternate.hreflang = alt.hreflang;
        linkAlternate.href = alt.href;
        document.head.appendChild(linkAlternate);
      }
    });

    // Add preconnect for performance
    const preconnectDomains = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://www.google-analytics.com',
    ];

    preconnectDomains.forEach(domain => {
      if (!document.querySelector(`link[rel="preconnect"][href="${domain}"]`)) {
        const preconnectLink = document.createElement('link');
        preconnectLink.setAttribute('rel', 'preconnect');
        preconnectLink.setAttribute('href', domain);
        document.head.appendChild(preconnectLink);
      }
    });

    // Add schema.org JSON-LD
    const schemas = generateSchema(title, description);
    if (schemas) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]:not([data-breadcrumb])');
      if (scriptTag) {
        scriptTag.textContent = JSON.stringify(schemas, null, 2);
      } else {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.textContent = JSON.stringify(schemas, null, 2);
        document.head.appendChild(scriptTag);
      }
    }

    // Cleanup function
    return () => {
      // Remove any dynamically added tags if needed
      // Note: Usually we keep SEO tags for the duration of the page visit
    };
  }, [effectiveTitle, effectiveDescription, canonicalUrl, effectiveSchemaType, schemaData, effectiveKeywords, ogImage, noIndex, alternateUrls, effectiveBreadcrumbs]);

  // This component doesn't render anything visible
  return null;
};

// Helper function to generate smart descriptions based on route params
const generateSmartDescription = (
  params: any,
  customPageData?: any
): string => {
  const { categorySlug, city, state, contractorId } = params;
  const { contractorCount } = customPageData || {};

  // Format display names
  const category = categorySlug?.replace(/-/g, ' ');
  const cityFormatted = city?.replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
  const stateFormatted = state?.toUpperCase();

  if (contractorId) {
    // Individual contractor page
    if (category && cityFormatted && stateFormatted) {
      return `Professional ${category} contractor in ${cityFormatted}, ${stateFormatted}. Read reviews, view portfolio, and request quotes for your project.`;
    }
    return `Professional contractor profile. Read reviews, view portfolio, and request quotes for your project.`;
  }

  if (category && cityFormatted && stateFormatted) {
    return `Find professional ${category} contractors in ${cityFormatted}, ${stateFormatted}. Browse reviews, contact info, and get competitive bids from licensed professionals. ${contractorCount ? `${contractorCount}+ contractors available.` : ''}`;
  }

  if (category && stateFormatted) {
    return `Find trusted ${category} contractors throughout ${stateFormatted}. Browse reviews, contact info, and get competitive bids from licensed professionals.`;
  }

  if (cityFormatted && stateFormatted) {
    return `Find qualified contractors in ${cityFormatted}, ${stateFormatted}. Browse our directory of professional contractors for your project needs.`;
  }

  if (category) {
    return `Find trusted ${category} contractors nationwide. Browse reviews, contact info, and get competitive bids from licensed professionals.`;
  }

  if (stateFormatted) {
    return `Find trusted contractors throughout ${stateFormatted}. Browse all service categories and get competitive bids from licensed professionals.`;
  }

  return 'Find trusted local contractors on BidBro. Browse by service category and location, read reviews, and get competitive bids from licensed professionals.';
};

// Helper function to generate SEO-friendly keywords
export const generateContractorKeywords = (
  category?: string,
  city?: string,
  state?: string,
  businessName?: string
): string[] => {
  const keywords = ['contractors', 'professional services', 'licensed contractors'];
  
  if (category) {
    const cleanCategory = category.replace(/-/g, ' ');
    keywords.push(cleanCategory, `${cleanCategory} contractors`, `professional ${cleanCategory}`);
  }
  
  if (city) {
    const cleanCity = city.replace(/-/g, ' ');
    keywords.push(`${cleanCity} contractors`, `contractors in ${cleanCity}`);
  }
  
  if (state) {
    keywords.push(`${state} contractors`, `contractors in ${state}`);
  }
  
  if (businessName) {
    keywords.push(businessName);
  }
  
  if (city && state) {
    const cleanCity = city.replace(/-/g, ' ');
    keywords.push(`contractors in ${cleanCity}, ${state}`);
  }
  
  return keywords;
};

// Helper function to generate meta descriptions
export const generateContractorDescription = (
  category?: string,
  city?: string,
  state?: string,
  businessName?: string,
  contractorCount?: number
): string => {
  if (businessName) {
    if (category && city && state) {
      const categoryClean = category.replace(/-/g, ' ');
      const cityClean = city.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const stateClean = state.toUpperCase();
      return `Contact ${businessName} for professional ${categoryClean} services in ${cityClean}, ${stateClean}. Read reviews, view portfolio, and request quotes for your project.`;
    } else {
      return `Contact ${businessName} for professional contractor services. Read reviews, view portfolio, and request quotes for your project.`;
    }
  }
  
  if (category && city && state) {
    const categoryClean = category.replace(/-/g, ' ');
    const cityClean = city.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const stateClean = state.toUpperCase();
    return `Find professional ${categoryClean} contractors in ${cityClean}, ${stateClean}. Browse reviews, contact info, and get competitive bids from licensed professionals. ${contractorCount ? `${contractorCount}+ contractors` : 'Multiple contractors'} available.`;
  } else if (category && state) {
    const categoryClean = category.replace(/-/g, ' ');
    const stateClean = state.toUpperCase();
    return `Find trusted ${categoryClean} throughout ${stateClean}. Browse reviews, contact info, and get competitive bids from licensed professionals in your area.`;
  } else if (city && state) {
    const cityClean = city.replace(/-/g, ' ');
    const stateClean = state.toUpperCase();
    return `Find trusted contractors in ${cityClean}, ${stateClean}. Browse all service categories, read reviews, and get competitive bids from licensed professionals.`;
  } else if (category) {
    const categoryClean = category.replace(/-/g, ' ');
    return `Find trusted ${categoryClean} in your area. Browse reviews, contact info, and get competitive bids from licensed professionals nationwide.`;
  } else if (state) {
    const stateClean = state.toUpperCase();
    return `Find trusted contractors throughout ${stateClean}. Browse all service categories, read reviews, and get competitive bids from licensed professionals.`;
  } else {
    return 'Find trusted local contractors on BidBro. Browse by service category and location, read reviews, and get competitive bids from licensed professionals.';
  }
};

// Helper function to generate page titles
export const generatePageTitle = (
  category?: string,
  city?: string,
  state?: string,
  businessName?: string,
  pageType: 'listing' | 'profile' | 'category' | 'location' = 'listing'
): string => {
  if (businessName) {
    return `${businessName} - Professional Contractor`;
  }
  
  if (category && city && state) {
    const categoryClean = category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const cityClean = city.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const stateClean = state.toUpperCase();
    return `${categoryClean} in ${cityClean}, ${stateClean}`;
  }
  
  if (category && state) {
    const categoryClean = category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const stateClean = state.toUpperCase();
    return `${categoryClean} in ${stateClean}`;
  }
  
  if (city && state) {
    const cityClean = city.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const stateClean = state.toUpperCase();
    return `Contractors in ${cityClean}, ${stateClean}`;
  }
  
  if (category) {
    const categoryClean = category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return `${categoryClean} Directory`;
  }
  
  if (state) {
    const stateClean = state.toUpperCase();
    return `Contractors in ${stateClean}`;
  }
  
  switch (pageType) {
    case 'category':
      return 'Contractor Categories Directory';
    case 'location':
      return 'Contractor Locations Directory';
    default:
      return 'Professional Contractor Directory';
  }
};

export default SEOTags;